import React, { memo, useMemo, useState } from "react";
import PostExertionResultRow from "../rows/PostExertionResultRow";
import { useMeasurementResults } from "../../../hooks/results/useMeasurementResults";
import {
  DEFAULT_RESULTS_PAGE_SIZE,
  useInfiniteResultsPageSize,
} from "../../../hooks/results/useInfiniteResultsPageSize";
import Spinner from "../../Spinner";
import MetricTrendChart from "../../charts/MetricTrendChart";
import { useTranslation } from "react-i18next";
import { SHOW_FULL_RESULTS } from "../../../config/results";
import { useResultRowNavigation } from "../../../hooks/results/useResultRowNavigation";
import { RESULT_MODE_CONFIG } from "../../../utils/results/resultModeConfig";
import ResultsTabState from "../ResultsTabState";
import { formatDayMonth, getLocalDayKeyMs } from "../../../utils/results/recordingIdTime";
import colours from "../../../theme/colours";

const CONFIG = RESULT_MODE_CONFIG.postExertion;

export default memo(function PostExertionResults({
  db,
  uid,
  firstRunByVariant,
}) {
  const { t, i18n } = useTranslation();
  const [pageSize, setPageSize] = useState(DEFAULT_RESULTS_PAGE_SIZE);

  const { rows, loading, loadingMore, hasMore, error } = useMeasurementResults(
    db,
    uid,
    {
      collectionName: CONFIG.collectionName,
      pageSize,
      orderField: "createdAt",
    },
  );

  const { handleScroll } = useInfiniteResultsPageSize({
    canLoadMore: hasMore,
    loading,
    loadingMore,
    rowsLength: rows.length,
    resetKey: `${uid || ""}:${CONFIG.resetKey}`,
    pageSize,
    setPageSize,
    initialPageSize: DEFAULT_RESULTS_PAGE_SIZE,
  });

  const onRowClick = useResultRowNavigation({
    variant: CONFIG.variant,
    context: CONFIG.context,
    tab: CONFIG.tab,
  });

  const trend = useMemo(() => {
    if (!Array.isArray(rows) || !rows.length) {
      return {
        labels: [],
        xValues: [],
        values: [],
        counts: [],
        minMax: [],
        minSeries: [],
        maxSeries: [],
      };
    }

    const byDay = new Map();

    [...rows].reverse().forEach((row) => {
      const ms = row?.timestamp ? Date.parse(row.timestamp) : NaN;
      if (!Number.isFinite(ms)) return;

      const dayStartMs = getLocalDayKeyMs(ms, i18n.language);
      if (!Number.isFinite(dayStartMs)) return;

      const current =
        byDay.get(dayStartMs) || {
          rrSum: 0,
          rrCount: 0,
          minSeries: null,
          maxSeries: null,
        };

      if (typeof row?.rr === "number" && Number.isFinite(row.rr)) {
        current.rrSum += row.rr;
        current.rrCount += 1;
      }

      if (typeof row?.rrMin === "number" && Number.isFinite(row.rrMin)) {
        current.minSeries =
          current.minSeries == null
            ? row.rrMin
            : Math.min(current.minSeries, row.rrMin);
      }

      if (typeof row?.rrMax === "number" && Number.isFinite(row.rrMax)) {
        current.maxSeries =
          current.maxSeries == null
            ? row.rrMax
            : Math.max(current.maxSeries, row.rrMax);
      }

      byDay.set(dayStartMs, current);
    });

    const days = Array.from(byDay.entries())
      .map(([dayStartMs, agg]) => ({ dayStartMs, ...agg }))
      .sort((a, b) => a.dayStartMs - b.dayStartMs);

    const xValues = days.map((day) => day.dayStartMs);
    const values = days.map((day) =>
      day.rrCount > 0 ? day.rrSum / day.rrCount : null,
    );
    const minSeries = days.map((day) => day.minSeries);
    const maxSeries = days.map((day) => day.maxSeries);

    return {
      labels: xValues.map((ms) => formatDayMonth(ms, i18n.language)),
      xValues,
      values,
      counts: days.map((day) => day.rrCount),
      minMax: days.map((day) =>
        day.minSeries != null && day.maxSeries != null
          ? { min: day.minSeries, max: day.maxSeries }
          : null,
      ),
      minSeries,
      maxSeries,
    };
  }, [rows, i18n.language]);

  return (
    <ResultsTabState
      uid={uid}
      loading={loading}
      error={error}
      rows={rows}
      emptyMode={CONFIG.mode}
      firstRunByVariant={firstRunByVariant}
    >
      <div className="w-full h-full min-h-0 flex flex-col gap-2">
        {SHOW_FULL_RESULTS && (
          <MetricTrendChart
            height={190}
            labels={trend?.labels}
            values={trend?.values}
            xValues={trend?.xValues}
            xTickFormat="dd/MM"
            lineLabel={t("result.postExertion.rrAverageLabel", "RR Avg")}
            yUnit="bpm"
            yDecimals={0}
            extraDatasets={[
              {
                label: t("result.postExertion.rrMinLabel", "RR Min"),
                data: trend?.minSeries,
                color: colours.tertiary,
              },
              {
                label: t("result.postExertion.rrMaxLabel", "RR Max"),
                data: trend?.maxSeries,
                color: colours.secondary,
              },
            ]}
            showMinMaxBars={true}
            minMax={trend?.minMax}
            counts={trend?.counts}
            showTrendLine={true}
          />
        )}

        <div
          aria-label="post exertion results list"
          className="w-full flex-1 min-h-0 overflow-y-auto"
          onScroll={handleScroll}
        >
          {rows.map((row, idx) => (
            <PostExertionResultRow
              key={row.id ?? idx}
              time={row.time}
              date={row.date}
              rr={row.rr}
              rrMin={row.rrMin}
              rrMax={row.rrMax}
              rrQuality={row.rrQuality}
              onClick={() => onRowClick(row)}
            />
          ))}
          {loadingMore ? <Spinner size={24} className="py-4" /> : null}
        </div>
      </div>
    </ResultsTabState>
  );
});
